import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Swal from 'sweetalert2';
import {
  Check,
  Minus,
  Plus,
  Search,
  ChevronDown,
  ChevronUp,
  ArrowDown
} from 'lucide-react';
import {
  FaWhatsapp,
  FaLocationCrosshairs,
  FaGlobe,
  FaHeadset,
  FaWandMagicSparkles,
  FaBuilding
} from 'react-icons/fa6';
import type { Plan } from '../types';

/* ==========================================================================
   PRICING DATA DEFINITIONS & FORMULAS
   ========================================================================== */
export type BillingCycle = 'monthly' | 'annual';

export type AddonKey =
  | 'whatsappCrm'
  | 'liveTracking'
  | 'customDomain'
  | 'prioritySla'
  | 'vipOnboarding'
  | 'additionalWorkspace';

export interface AddonSelection {
  whatsappCrm: boolean;
  liveTracking: boolean;
  customDomain: boolean;
  prioritySla: boolean;
  vipOnboarding: boolean;
  additionalWorkspace: boolean;
}

export const CYCLE_RATE = {
  monthly: 649,
  annual: 499,
} as const;

export const ADDON_PRICE = {
  whatsappCrm: 1999,
  liveTracking: 399,
  customDomain: 999,
  prioritySla: 1499,
  vipOnboarding: 2999,
  additionalWorkspace: 2000,
} as const;

export const PRIORITY_SLA_MIN_SEATS = 10;

export interface AddonMeta {
  key: AddonKey;
  name: string;
  tagline: string;
  description: string;
  price: number;
  priceFormatted: string;
  unit: string;
  scope: 'per-workspace' | 'per-employee' | 'account-wide' | 'one-time';
  scopeLabel: string;
  status: 'active' | 'soon';
  requiresMinSeats?: number;
}

export const ADDONS_CATALOG: AddonMeta[] = [
  {
    key: 'whatsappCrm',
    name: 'WhatsApp Business CRM & Shared Inbox',
    tagline: 'Meta WhatsApp Business API Integration',
    description: 'Official Meta Cloud API connection with shared team inbox, broadcast campaigns, pre-approved message templates, and automated follow-ups.',
    price: ADDON_PRICE.whatsappCrm,
    priceFormatted: '₹1,999',
    unit: '/ month',
    scope: 'per-workspace',
    scopeLabel: 'Per workspace · Dedicated phone number',
    status: 'active',
  },
  {
    key: 'liveTracking',
    name: 'Live GPS Field Staff Tracking',
    tagline: 'Real-time location, selfies & route maps',
    description: 'Track on-ground sales and support personnel in real time with GPS breadcrumb trails, selfie-verified site check-ins, and team map dashboard.',
    price: ADDON_PRICE.liveTracking,
    priceFormatted: '₹399',
    unit: '/ employee / month',
    scope: 'per-employee',
    scopeLabel: 'Per employee seat · Scales with team size',
    status: 'active',
  },
  {
    key: 'customDomain',
    name: 'Custom Domain & White-Label Portal',
    tagline: 'Your brand, your logo, your domain',
    description: 'Run Saasbuck on your own domain (e.g. crm.yourcompany.com) with custom login branding, email sender identity, and custom portal styling.',
    price: ADDON_PRICE.customDomain,
    priceFormatted: '₹999',
    unit: '/ month (or ₹9,999/yr)',
    scope: 'per-workspace',
    scopeLabel: 'Per workspace · Full custom SSL setup',
    status: 'active',
  },
  {
    key: 'prioritySla',
    name: 'Priority SLA & Dedicated Account Manager',
    tagline: '15-minute response SLA + VIP Slack/WhatsApp group',
    description: 'Dedicated relationship manager, priority tech support queue, personalized quarterly business reviews, and direct WhatsApp escalation bridge.',
    price: ADDON_PRICE.prioritySla,
    priceFormatted: '₹1,499',
    unit: '/ month',
    scope: 'per-workspace',
    scopeLabel: 'Per workspace · Requires 10+ user seats',
    status: 'active',
    requiresMinSeats: PRIORITY_SLA_MIN_SEATS,
  },
  {
    key: 'vipOnboarding',
    name: 'VIP Onboarding & White-Glove Migration',
    tagline: 'White-glove data import & staff training',
    description: 'Complete hands-on migration of your historical leads, client lists, invoices, and templates from your existing CRM or Excel sheets, plus live video staff training.',
    price: ADDON_PRICE.vipOnboarding,
    priceFormatted: '₹2,999',
    unit: 'one-time fee',
    scope: 'one-time',
    scopeLabel: 'One-time · Dedicated onboarding specialist',
    status: 'active',
  },
  {
    key: 'additionalWorkspace',
    name: 'Additional Workspace Expansion',
    tagline: 'Run multiple separate business brands',
    description: 'For business owners operating more than one distinct firm. Grants an additional workspace with its own dedicated 3GB storage while sharing your pooled seats. Repeatable — buy it again any time you need one more.',
    price: ADDON_PRICE.additionalWorkspace,
    priceFormatted: '₹2,000',
    unit: 'one-time fee',
    scope: 'account-wide',
    scopeLabel: 'Account-level · +1 workspace per purchase',
    status: 'active',
  },
];

/* Milestones for team seats slider */
const SEAT_MILESTONES = [
  { value: 1, label: '1 user' },
  { value: 25, label: '25 users' },
  { value: 50, label: '50 users' },
  { value: 75, label: '75 users' },
  { value: 100, label: '100+ users' },
];

/* 11 Core Platform Modules & 55 Features */
const CORE_PLATFORM_MODULES = [
  {
    title: 'Lead & Deal Management',
    items: [
      'Capture and store leads with contact details, source, and status',
      'Visual deal pipeline — both a Kanban board view and a spreadsheet-style list view',
      'Customizable pipeline stages (New, Contacted, Qualified, Proposal, Negotiation, Won, Lost)',
      'Assign leads/deals to specific team members with routing rules',
      'Bulk import leads from Excel/CSV',
      'Import leads automatically from Facebook/Meta Lead Ads forms',
      'Embeddable public inquiry forms for your website that feed leads directly in',
      'Deal value tracking and win/loss recording',
      'Tagging and filtering leads by any field',
      'Lead deduplication (won\'t create duplicate records for the same contact)'
    ]
  },
  {
    title: 'Client Management',
    items: [
      'Convert won deals into full client records with one click',
      'Store client company info, primary contacts, and billing profiles',
      'Map view of client locations with route optimization',
      'Full interaction history per client (calls, emails, meetings, notes)'
    ]
  },
  {
    title: 'Follow-Ups & Task Management',
    items: [
      'Schedule follow-ups (calls, emails, meetings, tasks) with due dates and reminders',
      'Mobile access for on-the-go follow-up execution & instant call logging',
      'Board view and list view for follow-ups, grouped by overdue / today / upcoming',
      'Log the outcome of each follow-up and auto-schedule the next one',
      'General task assignment and tracking, separate from follow-ups',
      'Shared calendar showing all follow-ups and tasks across the team'
    ]
  },
  {
    title: 'ERP — Invoicing & Payments',
    items: [
      'Generate professional invoices with itemized line items, tax, and discounts',
      'GST-ready tax calculation (per-item tax rates, automatically totaled)',
      'Quotations that convert directly into invoices',
      'Proforma invoices for pre-payment scenarios',
      'Record and track payments received against invoices',
      'PDF generation for invoices and quotations with company logo',
      'Product/service catalog for quick invoice line-item entry'
    ]
  },
  {
    title: 'Team & Employee Management',
    items: [
      'Employee\'s own personal panel (dedicated portal for each team member to manage assigned leads, daily tasks, follow-ups & incentives)',
      'Add employees/team members with role-based access control',
      'Fine-grained permissions (control exactly what each person can see/do)',
      'Departments and branches (for multi-location businesses)',
      'Employee performance targets and incentive/commission rules',
      'Employee attendance tracking & daily check-in summary',
      'Standard Operating Procedure (SOP) documents shared with the team'
    ]
  },
  {
    title: 'Reports & Analytics',
    items: [
      'Sales/pipeline performance dashboards with revenue forecasting',
      'Advanced analytics (conversion rates, team performance, lead source trends)',
      'Activity audit log (who did what, and when — full accountability trail)'
    ]
  },
  {
    title: 'Communication',
    items: [
      'Built-in team chat for real-time collaboration',
      'Bulk email sending to leads/clients with personalized tags',
      'Email history and delivery timeline per contact'
    ]
  },
  {
    title: 'Documents & Files',
    items: [
      'Document/file storage per client, lead, or deal',
      'Folder organization for agreements, proposal drafts and IDs',
      'Cloud storage (with dedicated quota per workspace)'
    ]
  },
  {
    title: 'Automation & Workflow',
    items: [
      'Automated workflow triggers (status-change actions, auto-assignment)',
      'Custom fields — add your own data fields to leads/deals/clients without coding',
      'Custom pipeline stages tailored to your sales process'
    ]
  },
  {
    title: 'Account, Security & Workspace Access',
    items: [
      'Full mobile access (native Android APK app & responsive mobile web app on iOS & Android)',
      'Real-time cross-device sync between web, mobile & tablet',
      'Instant mobile alerts and push notifications for urgent follow-ups & incoming leads',
      'Multi-workspace support (one account, multiple businesses/branches)',
      'Session management (see and log out of active devices)',
      'Two-factor-style OTP verification on sign-in',
      'Trash/recycle bin — restore or permanently delete records, with an admin-only hard-delete'
    ]
  },
  {
    title: 'Integrations & API',
    items: [
      'API webhooks (connect Synapse to external webhooks and apps)',
      'Full data export in Excel/CSV formats (take your data with you anytime)'
    ]
  }
];

/* ==========================================================================
   COMPONENT PROPS
   ========================================================================== */
interface PlansManagementViewProps {
  selectedSoftware: 'Synapse' | 'Co-HR' | null;
  plans: Plan[];
  isPlansLoading: boolean;
  featureDefinitions?: Record<string, any>;
  onApplyPlanToClient?: (config: {
    seats: number;
    billingCycle: BillingCycle;
    addons: AddonSelection;
    total: number;
  }) => void;
}

export const PlansManagementView: React.FC<PlansManagementViewProps> = ({
  selectedSoftware,
  plans = [],
  isPlansLoading = false,
  featureDefinitions,
  onApplyPlanToClient
}) => {
  const [seats, setSeats] = useState<number>(5);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('annual');
  const [addons, setAddons] = useState<AddonSelection>({
    whatsappCrm: false,
    liveTracking: false,
    customDomain: false,
    prioritySla: false,
    vipOnboarding: false,
    additionalWorkspace: false,
  });

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    'Lead & Deal Management': true,
    'Client Management': true,
    'ERP — Invoicing & Payments': true,
  });

  const targetSwKey = selectedSoftware || 'Synapse';
  const apiDefinitionsCount = featureDefinitions && featureDefinitions[targetSwKey]
    ? featureDefinitions[targetSwKey].length
    : 0;

  // Auto-deselect Priority SLA if seats < 10
  useEffect(() => {
    if (addons.prioritySla && seats < PRIORITY_SLA_MIN_SEATS) {
      setAddons((prev) => ({ ...prev, prioritySla: false }));
    }
  }, [seats, addons.prioritySla]);

  if (isPlansLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '4rem 1rem', color: '#94a3b8', fontWeight: 400 }}>
        <div>Loading plan packages &amp; entitlement specifications...</div>
      </div>
    );
  }

  const toggleAddon = (key: AddonKey) => {
    if (key === 'prioritySla' && seats < PRIORITY_SLA_MIN_SEATS) {
      Swal.fire({
        title: 'Minimum Seats Required',
        text: 'Priority SLA & Dedicated Account Manager requires at least 10 user seats.',
        icon: 'info',
        confirmButtonColor: '#2563eb'
      });
      return;
    }
    setAddons((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSeatChange = (val: number) => {
    if (val < 1) {
      setSeats(1);
      return;
    }
    if (val > 100) {
      setSeats(100);
      return;
    }
    setSeats(val);
  };

  const toggleCategory = (title: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const expandAll = () => {
    const allOpen: Record<string, boolean> = {};
    CORE_PLATFORM_MODULES.forEach((c) => {
      allOpen[c.title] = true;
    });
    setExpandedCategories(allOpen);
  };

  const collapseAll = () => {
    setExpandedCategories({});
  };

  const allAreExpanded = CORE_PLATFORM_MODULES.every((c) => expandedCategories[c.title]);
  const totalFeatures = CORE_PLATFORM_MODULES.reduce((sum, c) => sum + c.items.length, 0);

  /* Calculations */
  const perUserRate = CYCLE_RATE[billingCycle];
  const cycleMonths = billingCycle === 'annual' ? 12 : 1;

  let addonMonthly = 0;
  if (addons.whatsappCrm) addonMonthly += ADDON_PRICE.whatsappCrm;
  if (addons.liveTracking) addonMonthly += ADDON_PRICE.liveTracking * seats;
  if (addons.customDomain) addonMonthly += ADDON_PRICE.customDomain;
  if (addons.prioritySla) addonMonthly += ADDON_PRICE.prioritySla;

  const baseRecurring = perUserRate * seats * cycleMonths;
  const addonsRecurring = addonMonthly * cycleMonths;
  const recurringTotal = baseRecurring + addonsRecurring;
  const oneTimeTotal =
    (addons.vipOnboarding ? ADDON_PRICE.vipOnboarding : 0) +
    (addons.additionalWorkspace ? ADDON_PRICE.additionalWorkspace : 0);
  const total = recurringTotal + oneTimeTotal;
  const monthlyEquivalent = Math.round((recurringTotal / cycleMonths) + (oneTimeTotal ? oneTimeTotal / 12 : 0));
  const annualSavingsTotal = (CYCLE_RATE.monthly - CYCLE_RATE.annual) * seats * 12;

  // Selected add-ons for the itemized particulars summary
  const selectedAddonsList = ADDONS_CATALOG.filter((addon) => addons[addon.key]).map((addon) => {
    let calculatedAmount = 0;
    if (addon.scope === 'one-time') {
      calculatedAmount = addon.price;
    } else if (addon.key === 'liveTracking') {
      calculatedAmount = addon.price * seats * (billingCycle === 'annual' ? 12 : 1);
    } else {
      calculatedAmount = addon.price * (billingCycle === 'annual' ? 12 : 1);
    }
    return {
      ...addon,
      calculatedAmount,
    };
  });

  const filteredCategories = CORE_PLATFORM_MODULES
    .map((cat) => ({
      ...cat,
      items: cat.items.filter(
        (item) =>
          item.toLowerCase().includes(searchQuery.toLowerCase()) ||
          cat.title.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    }))
    .filter((cat) => cat.items.length > 0);

  /* Sales Pitch Copy for Staff */
  const handleCopySalesPitch = () => {
    const addonsText = selectedAddonsList.length > 0
      ? selectedAddonsList.map(a => `• ${a.name}: ₹${a.calculatedAmount.toLocaleString('en-IN')}`).join('\n')
      : '• No optional add-ons selected (pure core CRM)';

    const pitch = `🚀 *Saasbuck Core CRM & ERP Platform*
Comprehensive all-in-one business operations, deal management, GST invoicing & field mobility suite.

💰 *Pricing Model:* ₹${perUserRate}/user/month (${billingCycle === 'annual' ? 'Billed annually at ₹5,988/user/year upfront with 23% savings' : 'Billed monthly, flexible pay-as-you-go'})

👥 *Configured Team Seats:* ${seats} User ${seats === 1 ? 'Seat' : 'Seats'} (Pooled across all workspaces)

✨ *Package Inclusions & Quotas:*
• Contacts Capacity: 20,000 Contacts
• Cloud Storage: 3 GB Included per workspace
• Pipeline Stages: Unlimited Customizable Stages
• ERP Suite: GST Invoicing, Proforma, Quotations & Payment Tracking
• Mobile Access: Native Android App + Responsive Web

🔑 *Selected Add-On Capabilities:*
${addonsText}

📦 *Platform Modules Included (11 Modules, 55 Features):*
• Lead & Deal Pipeline (Kanban + Spreadsheet view)
• Client Records & Interaction Timeline
• Follow-ups, Due Reminders & Task Board
• Professional Invoices, Taxes & Payment Records
• Team Management, SOPs & Dedicated Employee Panel
• Analytics, Revenue Trends & Activity Audit Log

💵 *Commercial Summary:*
Total Amount: ₹${total.toLocaleString('en-IN')} ${billingCycle === 'annual' ? '(12 months upfront)' : '/ month'}
Monthly Equivalent: ≈ ₹${monthlyEquivalent.toLocaleString('en-IN')}/month

Ready for instant workspace provisioning! Contact our support team to get activated today.`;

    navigator.clipboard.writeText(pitch);
    Swal.fire({
      title: 'Sales Pitch Copied!',
      text: 'Formatted WhatsApp/chat pitch has been copied to your clipboard.',
      icon: 'success',
      timer: 2000,
      showConfirmButton: false
    });
  };

  /* Formal Client Quotation Copy */
  const handleCopyQuotation = () => {
    const today = new Date().toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });

    const addonsText = selectedAddonsList.length > 0
      ? selectedAddonsList.map(a => `   - ${a.name}: ₹${a.calculatedAmount.toLocaleString('en-IN')} (${a.scopeLabel})`).join('\n')
      : '   - (None)';

    const gstAmount = Math.round(total * 0.18);
    const grandTotal = total + gstAmount;

    const quotation = `====================================================
           COMMERCIAL QUOTATION / PROPOSAL
                  SAASBUCK PLATFORM
====================================================
Date of Issue: ${today}
Software Platform: ${selectedSoftware === 'Co-HR' ? 'Co-HR Suite' : 'Synapse CRM & ERP Platform'}
Subscription License: Unified Core Commercial Plan

PARTICULARS & FEE BREAKDOWN:
----------------------------------------------------
1. Base Platform License:
   - Configured Seats: ${seats} User Seats
   - Billing Frequency: ${billingCycle.toUpperCase()} (₹${perUserRate}/user/mo)
   - Base Amount: ₹${baseRecurring.toLocaleString('en-IN')}

2. Optional Add-On Extensions:
${addonsText}

----------------------------------------------------
Subtotal (Pre-Tax): ₹${total.toLocaleString('en-IN')}
${billingCycle === 'annual' ? `Annual Discount Applied (23% Off): -₹${annualSavingsTotal.toLocaleString('en-IN')}\n` : ''}Estimated GST (18%): ₹${gstAmount.toLocaleString('en-IN')}
----------------------------------------------------
GRAND TOTAL (INCL. GST): ₹${grandTotal.toLocaleString('en-IN')}
Amortized Equivalent: ≈ ₹${monthlyEquivalent.toLocaleString('en-IN')}/month
----------------------------------------------------

COMMERCIAL TERMS & INCLUSIONS:
• Includes all 11 Core Platform Modules (55 features)
• Pooled user seats shared across all workspaces
• 3 GB cloud storage per workspace
• Multi-device mobile app and push notification access
• Standard 99.9% platform uptime commitment
• Official GST invoice generated upon activation

Validity: 14 business days from issue date.
====================================================`;

    navigator.clipboard.writeText(quotation);
    Swal.fire({
      title: 'Quotation Copied!',
      text: 'Itemized commercial proposal copied with GST & terms.',
      icon: 'success',
      timer: 2000,
      showConfirmButton: false
    });
  };

  const clampedSeats = Math.min(100, Math.max(1, seats));
  const seatRatio = (clampedSeats - 1) / 99;

  return (
    <div className="modern-plans-wrapper">
      
      {/* ================================================================= */}
      {/* 1. TOP HEADER & TITLE (ICON-LESS, CLEAN, UNBOLDED)               */}
      {/* ================================================================= */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '1.25rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.65rem', fontWeight: 400, color: '#0f172a', letterSpacing: '-0.02em', lineHeight: 1.25 }}>
            Plan Packages &amp; Pricing Architecture
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '0.35rem' }}>
            <p style={{ margin: 0, fontSize: '0.88rem', color: '#64748b', fontWeight: 400 }}>
              Single core license model with pooled staffing capacity, isolated workspace storage, and modular add-on specifications.
            </p>
            {plans.length > 0 && (
              <span style={{ fontSize: '11px', fontWeight: 400, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', padding: '1px 7px', borderRadius: '4px' }}>
                {plans[0].name || 'Growth Core'} ({plans[0].code || 'growth'}) Active
              </span>
            )}
          </div>
        </div>

        {/* Action Buttons for Help Desk Staff (Clean, Icon-less) */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          {onApplyPlanToClient && (
            <button
              type="button"
              onClick={() => onApplyPlanToClient({ seats, billingCycle, addons, total })}
              className="btn-primary-3d"
              style={{ background: 'linear-gradient(to bottom, #059669, #047857) !important', borderColor: '#047857', fontWeight: 400 }}
            >
              <span>Apply to Client License</span>
            </button>
          )}
          <button
            type="button"
            onClick={handleCopySalesPitch}
            className="btn-secondary-3d"
            style={{ fontWeight: 400 }}
            title="Copy sales pitch summary formatted for WhatsApp/Email"
          >
            <span>Copy Sales Pitch</span>
          </button>

          <button
            type="button"
            onClick={handleCopyQuotation}
            className="btn-primary-3d"
            style={{ fontWeight: 400 }}
            title="Generate & copy formal itemized quotation breakdown with GST"
          >
            <span>Generate Client Quote</span>
          </button>
        </div>
      </div>

      {/* ================================================================= */}
      {/* 2. INTERACTIVE RATE CALCULATOR & CYCLE SWITCHER                   */}
      {/* ================================================================= */}
      <div className="modern-plans-card modern-plans-card-pad" style={{ background: '#ffffff', borderRadius: '4px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '1.25rem', marginBottom: '1.5rem' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 400, color: '#0f172a' }}>
              Interactive Commercial Calculator
            </h2>
            <p style={{ margin: '0.2rem 0 0', fontSize: '0.82rem', color: '#64748b', fontWeight: 400 }}>
              Adjust billing frequency and user seat count to preview dynamic pricing in real time:
            </p>
          </div>

          {/* 3D Tactile Billing Cycle Switcher */}
          <div className="tactile-cycle-container">
            <button
              type="button"
              onClick={() => setBillingCycle('annual')}
              className={`tactile-cycle-btn ${billingCycle === 'annual' ? 'active' : 'inactive'}`}
              style={{ fontWeight: 400 }}
            >
              {billingCycle === 'annual' && (
                <motion.div
                  layoutId="activeCyclePillHelpDesk"
                  transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                  className="tactile-cycle-pill"
                />
              )}
              <span style={{ position: 'relative', zIndex: 11, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <span>Annual (₹499/mo)</span>
                <span style={{
                  fontSize: '10px',
                  fontWeight: 400,
                  padding: '2px 7px',
                  borderRadius: '9999px',
                  background: billingCycle === 'annual' ? 'rgba(255, 255, 255, 0.25)' : '#dcfce7',
                  color: billingCycle === 'annual' ? '#ffffff' : '#15803d',
                  border: billingCycle === 'annual' ? '1px solid rgba(255, 255, 255, 0.4)' : '1px solid #bbf7d0',
                }}>
                  Save 23%
                </span>
              </span>
            </button>

            <button
              type="button"
              onClick={() => setBillingCycle('monthly')}
              className={`tactile-cycle-btn ${billingCycle === 'monthly' ? 'active' : 'inactive'}`}
              style={{ fontWeight: 400 }}
            >
              {billingCycle === 'monthly' && (
                <motion.div
                  layoutId="activeCyclePillHelpDesk"
                  transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                  className="tactile-cycle-pill"
                />
              )}
              <span style={{ position: 'relative', zIndex: 11 }}>
                Monthly (₹649/mo)
              </span>
            </button>
          </div>
        </div>

        {/* Stepper & Slider Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2.5rem', alignItems: 'center' }}>
          
          {/* Left Column: Base Rate Display (Unbolded) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <div style={{ fontSize: '0.8125rem', fontWeight: 500, color: '#64748b', textTransform: 'none' }}>
              Base license rate
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
              <span style={{ fontSize: '2.5rem', fontWeight: 400, color: '#0f172a', letterSpacing: '-0.03em', lineHeight: 1 }}>
                ₹{perUserRate}
              </span>
              <span style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 400 }}>/ user / month</span>
            </div>
            <div style={{ fontSize: '0.82rem', color: '#64748b', lineHeight: 1.4, marginTop: '2px', fontWeight: 400 }}>
              {billingCycle === 'annual'
                ? 'Billed annually at ₹5,988/user/year upfront. Covers account owner + pooled team seats.'
                : 'Billed monthly. Pay as you go with no lock-in.'}
            </div>
          </div>

          {/* Right Column: Interactive Seats Stepper & 3D Slider */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.88rem', fontWeight: 400, color: '#334155' }}>
                Pooled Team Seats:
              </span>

              {/* Stepper */}
              <div className="seat-stepper-pill">
                <button
                  type="button"
                  onClick={() => handleSeatChange(seats - 1)}
                  disabled={seats <= 1}
                  className="seat-stepper-btn minus"
                  title="Decrease seats"
                >
                  <Minus size={15} />
                </button>
                <span style={{ width: '40px', textAlign: 'center', fontWeight: 400, fontSize: '1.2rem', color: '#0f172a' }}>
                  {seats}
                </span>
                <button
                  type="button"
                  onClick={() => handleSeatChange(seats + 1)}
                  disabled={seats >= 100}
                  className="seat-stepper-btn plus"
                  title="Increase seats"
                >
                  <Plus size={15} />
                </button>
              </div>
            </div>

            {/* 3D Tactile Range Slider */}
            <div style={{ position: 'relative', padding: '4px 0' }}>
              <input
                type="range"
                min="1"
                max="100"
                value={clampedSeats}
                onChange={(e) => handleSeatChange(Number(e.target.value))}
                aria-label="Team Seats Slider"
                className="seat-slider-3d"
                style={{
                  background: `linear-gradient(to right, #2563eb 0%, #3b82f6 calc(13px + ${seatRatio} * (100% - 26px)), #dbe2ea calc(13px + ${seatRatio} * (100% - 26px)), #dbe2ea 100%)`,
                }}
              />

              {/* Exact Milestone Ticks and Labels */}
              <div style={{ position: 'relative', width: '100%', height: '48px', marginTop: '8px', userSelect: 'none' }}>
                {SEAT_MILESTONES.map((m, idx) => {
                  const isReached = seats >= m.value;
                  const isSelected = seats === m.value;
                  const ratio = (m.value - 1) / 99;

                  let labelAlign: React.CSSProperties = { textAlign: 'center' };
                  if (idx === 0) labelAlign = { textAlign: 'left', transform: 'translateX(6px)' };
                  if (idx === SEAT_MILESTONES.length - 1) labelAlign = { textAlign: 'right', transform: 'translateX(-6px)' };

                  return (
                    <div
                      key={m.value}
                      onClick={() => handleSeatChange(m.value)}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: `calc(13px + ${ratio} * (100% - 26px))`,
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      {/* Tick Notch */}
                      <div
                        style={{
                          width: '2px',
                          height: isSelected ? '8px' : '5px',
                          borderRadius: '9999px',
                          marginBottom: '4px',
                          background: isSelected ? '#2563eb' : isReached ? '#3b82f6' : '#cbd5e1',
                          transition: 'all 0.15s ease'
                        }}
                      />

                      {/* Checkmark Badge */}
                      <div
                        style={{
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: `1.5px solid ${isSelected ? '#2563eb' : isReached ? '#3b82f6' : '#cbd5e1'}`,
                          background: isSelected ? '#2563eb' : isReached ? '#3b82f6' : '#f8fafc',
                          color: '#ffffff',
                          boxShadow: isSelected ? '0 0 0 3px rgba(37,99,235,0.25)' : 'none',
                          marginBottom: '3px',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        {isReached && <Check size={10} strokeWidth={3} />}
                      </div>

                      {/* Milestone Label (Unbolded) */}
                      <span
                        style={{
                          fontSize: '11px',
                          whiteSpace: 'nowrap',
                          color: isSelected ? '#2563eb' : isReached ? '#1e293b' : '#94a3b8',
                          fontWeight: 400,
                          ...labelAlign
                        }}
                      >
                        {m.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Add-on Navigation Banner in Calculator */}
        <div style={{
          marginTop: '1.25rem',
          paddingTop: '1.25rem',
          borderTop: '1px solid #f1f5f9',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem',
          fontSize: '0.84rem',
          color: '#64748b'
        }}>
          <span style={{ fontWeight: 400 }}>Looking to bundle WhatsApp Business CRM, Live GPS Tracking, or dedicated SLA?</span>
          <button
            type="button"
            onClick={() => {
              document.getElementById('helpdesk-addons-section')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="btn-primary-3d"
            style={{ padding: '7px 18px', fontSize: '12.5px', fontWeight: 400 }}
          >
            <span>Configure Add-On Options</span>
            <ArrowDown size={13} />
          </button>
        </div>
      </div>

      {/* ================================================================= */}
      {/* 3. PRICING SPECIFICATION & ENTITLEMENT ARCHITECTURE TABLE        */}
      {/* ================================================================= */}
      <div>
        <div style={{ marginBottom: '0.75rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 400, color: '#0f172a' }}>
            Pricing Specification &amp; Entitlement Architecture
          </h2>
          <p style={{ margin: '0.2rem 0 0', fontSize: '0.82rem', color: '#64748b', fontWeight: 400 }}>
            Official platform catalog matching backend quota enforcement and licensing constraints:
          </p>
        </div>

        <div className="modern-plans-card" style={{ overflowX: 'auto', borderRadius: '4px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem', textAlign: 'left' }}>
            <thead style={{ background: '#ffffff' }}>
              <tr style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                <th style={{ padding: '0.85rem 1.15rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none' }}>Item / Service</th>
                <th style={{ padding: '0.85rem 1.15rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none' }}>Price Specification</th>
                <th style={{ padding: '0.85rem 1.15rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none' }}>Billing Unit</th>
                <th style={{ padding: '0.85rem 1.15rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none' }}>Workspace Pooling</th>
                <th style={{ padding: '0.85rem 1.15rem', background: '#ffffff', fontWeight: 500, color: '#475569', textTransform: 'none' }}>Enforcement Status</th>
              </tr>
            </thead>
            <tbody style={{ color: '#334155', fontWeight: 400 }}>
              <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '0.85rem 1.15rem', fontWeight: 400, color: '#1a73e8' }}>Synapse Core CRM (Base Plan)</td>
                <td style={{ padding: '0.85rem 1.15rem', fontWeight: 400 }}>₹499/mo (Annual) · ₹649/mo (Monthly)</td>
                <td style={{ padding: '0.85rem 1.15rem' }}>Per user</td>
                <td style={{ padding: '0.85rem 1.15rem', color: '#15803d' }}>Covers account owner</td>
                <td style={{ padding: '0.85rem 1.15rem' }}>
                  <span style={{ background: '#dcfce7', color: '#15803d', fontSize: '11px', fontWeight: 400, padding: '2px 8px', borderRadius: '4px' }}>
                    Active &amp; Live
                  </span>
                </td>
              </tr>
              <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '0.85rem 1.15rem', fontWeight: 400, color: '#1a73e8' }}>Additional Staff Seat</td>
                <td style={{ padding: '0.85rem 1.15rem', fontWeight: 400 }}>₹499/mo (Annual) · ₹649/mo (Monthly)</td>
                <td style={{ padding: '0.85rem 1.15rem' }}>Per additional staff</td>
                <td style={{ padding: '0.85rem 1.15rem', color: '#15803d' }}>✓ Pools across all workspaces</td>
                <td style={{ padding: '0.85rem 1.15rem' }}>
                  <span style={{ background: '#dcfce7', color: '#15803d', fontSize: '11px', fontWeight: 400, padding: '2px 8px', borderRadius: '4px' }}>
                    Active &amp; Live
                  </span>
                </td>
              </tr>
              <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '0.85rem 1.15rem', fontWeight: 400, color: '#1a73e8' }}>Cloud Storage</td>
                <td style={{ padding: '0.85rem 1.15rem', fontWeight: 400 }}>3 GB Included (No upgrade fee)</td>
                <td style={{ padding: '0.85rem 1.15rem' }}>Per workspace</td>
                <td style={{ padding: '0.85rem 1.15rem', color: '#64748b' }}>Fixed 3GB each (Isolated)</td>
                <td style={{ padding: '0.85rem 1.15rem' }}>
                  <span style={{ background: '#dcfce7', color: '#15803d', fontSize: '11px', fontWeight: 400, padding: '2px 8px', borderRadius: '4px' }}>
                    Active &amp; Live
                  </span>
                </td>
              </tr>
              <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '0.85rem 1.15rem', fontWeight: 400, color: '#1a73e8' }}>WhatsApp Business CRM &amp; Inbox</td>
                <td style={{ padding: '0.85rem 1.15rem', fontWeight: 400 }}>₹1,999 / mo</td>
                <td style={{ padding: '0.85rem 1.15rem' }}>Flat per phone number</td>
                <td style={{ padding: '0.85rem 1.15rem', color: '#64748b' }}>Per workspace number</td>
                <td style={{ padding: '0.85rem 1.15rem' }}>
                  <span style={{ background: '#dcfce7', color: '#15803d', fontSize: '11px', fontWeight: 400, padding: '2px 8px', borderRadius: '4px' }}>
                    Active &amp; Live
                  </span>
                </td>
              </tr>
              <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '0.85rem 1.15rem', fontWeight: 400, color: '#1a73e8' }}>Live GPS Field Staff Tracking</td>
                <td style={{ padding: '0.85rem 1.15rem', fontWeight: 400 }}>₹399 / mo</td>
                <td style={{ padding: '0.85rem 1.15rem' }}>Per employee seat</td>
                <td style={{ padding: '0.85rem 1.15rem', color: '#64748b' }}>Per workspace</td>
                <td style={{ padding: '0.85rem 1.15rem' }}>
                  <span style={{ background: '#dcfce7', color: '#15803d', fontSize: '11px', fontWeight: 400, padding: '2px 8px', borderRadius: '4px' }}>
                    Active &amp; Live
                  </span>
                </td>
              </tr>
              <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '0.85rem 1.15rem', fontWeight: 400, color: '#1a73e8' }}>Custom Domain &amp; White-Label</td>
                <td style={{ padding: '0.85rem 1.15rem', fontWeight: 400 }}>₹999/mo (or ₹9,999/yr)</td>
                <td style={{ padding: '0.85rem 1.15rem' }}>Flat</td>
                <td style={{ padding: '0.85rem 1.15rem', color: '#64748b' }}>Per workspace</td>
                <td style={{ padding: '0.85rem 1.15rem' }}>
                  <span style={{ background: '#e0f2fe', color: '#0369a1', fontSize: '11px', fontWeight: 400, padding: '2px 8px', borderRadius: '4px' }}>
                    Active
                  </span>
                </td>
              </tr>
              <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '0.85rem 1.15rem', fontWeight: 400, color: '#1a73e8' }}>Priority SLA &amp; Dedicated RM</td>
                <td style={{ padding: '0.85rem 1.15rem', fontWeight: 400 }}>₹1,499 / mo</td>
                <td style={{ padding: '0.85rem 1.15rem' }}>Flat (requires 10+ seats)</td>
                <td style={{ padding: '0.85rem 1.15rem', color: '#64748b' }}>Per workspace</td>
                <td style={{ padding: '0.85rem 1.15rem' }}>
                  <span style={{ background: '#e0f2fe', color: '#0369a1', fontSize: '11px', fontWeight: 400, padding: '2px 8px', borderRadius: '4px' }}>
                    Active
                  </span>
                </td>
              </tr>
              <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '0.85rem 1.15rem', fontWeight: 400, color: '#1a73e8' }}>VIP Onboarding &amp; Migration</td>
                <td style={{ padding: '0.85rem 1.15rem', fontWeight: 400 }}>₹2,999 flat</td>
                <td style={{ padding: '0.85rem 1.15rem' }}>One-time fee</td>
                <td style={{ padding: '0.85rem 1.15rem', color: '#64748b' }}>Per workspace</td>
                <td style={{ padding: '0.85rem 1.15rem' }}>
                  <span style={{ background: '#e0f2fe', color: '#0369a1', fontSize: '11px', fontWeight: 400, padding: '2px 8px', borderRadius: '4px' }}>
                    Active
                  </span>
                </td>
              </tr>
              <tr>
                <td style={{ padding: '0.85rem 1.15rem', fontWeight: 400, color: '#1a73e8' }}>Additional Workspace Expansion</td>
                <td style={{ padding: '0.85rem 1.15rem', fontWeight: 400 }}>₹2,000 flat</td>
                <td style={{ padding: '0.85rem 1.15rem' }}>One-time fee</td>
                <td style={{ padding: '0.85rem 1.15rem', color: '#64748b' }}>Account-level (+1 workspace with 3GB)</td>
                <td style={{ padding: '0.85rem 1.15rem' }}>
                  <span style={{ background: '#e0f2fe', color: '#0369a1', fontSize: '11px', fontWeight: 400, padding: '2px 8px', borderRadius: '4px' }}>
                    Active
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ================================================================= */}
      {/* 4. INTERACTIVE ADD-ON OPTIONS CATALOG (ROUNDED CIRCULAR ICONS)   */}
      {/* ================================================================= */}
      <div id="helpdesk-addons-section">
        <div style={{ marginBottom: '0.75rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 400, color: '#0f172a' }}>
            Interactive Add-On Options Catalog
          </h2>
          <p style={{ margin: '0.2rem 0 0', fontSize: '0.82rem', color: '#64748b', fontWeight: 400 }}>
            Toggle optional features to automatically compute real-time quotes and staff pitches:
          </p>
        </div>

        <div className="modern-plans-card" style={{ display: 'flex', flexDirection: 'column', borderRadius: '4px' }}>
          {ADDONS_CATALOG.map((addon, index) => {
            const isSelected = addons[addon.key];
            const isDisabled = addon.requiresMinSeats ? seats < addon.requiresMinSeats : false;

            let priceDisplay = addon.priceFormatted;
            let unitText = addon.unit;
            if (addon.key === 'liveTracking') {
              priceDisplay = `₹${(addon.price * seats).toLocaleString('en-IN')}`;
              unitText = `/ month (${seats} seats)`;
            }

            return (
              <div
                key={addon.key}
                onClick={() => !isDisabled && toggleAddon(addon.key)}
                style={{
                  padding: '1.15rem 1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '1rem',
                  borderBottom: index < ADDONS_CATALOG.length - 1 ? '1px solid #f1f5f9' : 'none',
                  background: isDisabled ? '#f8fafc' : isSelected ? '#f8faff' : '#ffffff',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  opacity: isDisabled ? 0.5 : 1,
                  transition: 'background-color 0.15s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  {/* Ant Design Style Checkbox */}
                  <span
                    className={`ant-mock-checkbox ${isSelected ? 'checked' : ''} ${isDisabled ? 'disabled' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isDisabled) toggleAddon(addon.key);
                    }}
                  >
                    {isSelected && <Check size={13} color="#ffffff" strokeWidth={3} />}
                  </span>

                  {/* PERFECTLY ROUND CIRCULAR ICON BADGE (borderRadius: 50%) */}
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    background: addon.key === 'whatsappCrm' ? '#ecfdf5'
                      : addon.key === 'liveTracking' ? '#eff6ff'
                      : addon.key === 'customDomain' ? '#eef2ff'
                      : addon.key === 'prioritySla' ? '#fffbeb'
                      : addon.key === 'vipOnboarding' ? '#faf5ff'
                      : '#f0f9ff',
                    border: `1px solid ${
                      addon.key === 'whatsappCrm' ? '#a7f3d0'
                      : addon.key === 'liveTracking' ? '#bfdbfe'
                      : addon.key === 'customDomain' ? '#c7d2fe'
                      : addon.key === 'prioritySla' ? '#fde68a'
                      : addon.key === 'vipOnboarding' ? '#e9d5ff'
                      : '#bae6fd'
                    }`,
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)'
                  }}>
                    {addon.key === 'whatsappCrm' && <FaWhatsapp size={22} color="#25D366" />}
                    {addon.key === 'liveTracking' && <FaLocationCrosshairs size={20} color="#2563eb" />}
                    {addon.key === 'customDomain' && <FaGlobe size={19} color="#4f46e5" />}
                    {addon.key === 'prioritySla' && <FaHeadset size={20} color="#d97706" />}
                    {addon.key === 'vipOnboarding' && <FaWandMagicSparkles size={19} color="#9333ea" />}
                    {addon.key === 'additionalWorkspace' && <FaBuilding size={19} color="#0284c7" />}
                  </div>

                  {/* Title & Description (Unbolded) */}
                  <div>
                    <div style={{ fontSize: '0.95rem', fontWeight: 400, color: '#0f172a' }}>
                      {addon.name}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px', lineHeight: 1.35, fontWeight: 400 }}>
                      {addon.description}
                    </div>
                    {isDisabled && (
                      <div style={{ fontSize: '0.74rem', color: '#b45309', fontWeight: 400, marginTop: '3px' }}>
                        Requires at least {addon.requiresMinSeats} seats (currently {seats})
                      </div>
                    )}
                  </div>
                </div>

                {/* Price Display (Unbolded) */}
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: '1rem', fontWeight: 400, color: '#0f172a' }}>
                    {priceDisplay}
                  </div>
                  <div style={{ fontSize: '0.74rem', color: '#94a3b8', fontWeight: 400 }}>
                    {unitText}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ================================================================= */}
      {/* 5. ORDER SUMMARY & PERFORATED RECEIPT (LESS ROUNDED, UNBOLDED)    */}
      {/* ================================================================= */}
      <div className="receipt-box" style={{ borderRadius: '4px' }}>
        {/* Receipt Header (Icon-less) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', borderBottom: '2px dashed #cbd5e1', paddingBottom: '1.25rem' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 400, color: '#0f172a' }}>
              Quotation &amp; Order Summary
            </h2>
            <p style={{ margin: '0.2rem 0 0', fontSize: '0.8rem', color: '#64748b', fontWeight: 400 }}>
              Itemized commercial calculation for client provisioning and license management:
            </p>
          </div>

          <span style={{
            fontSize: '11px',
            fontWeight: 400,
            padding: '3px 10px',
            borderRadius: '9999px',
            background: billingCycle === 'annual' ? '#dcfce7' : '#eff6ff',
            color: billingCycle === 'annual' ? '#15803d' : '#1d4ed8',
            border: `1px solid ${billingCycle === 'annual' ? '#bbf7d0' : '#bfdbfe'}`
          }}>
            {billingCycle === 'annual' ? 'Billed Annually (Save 23%)' : 'Billed Monthly'}
          </span>
        </div>

        {/* Itemized Lines */}
        <div style={{ marginTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.85rem' }}>
          {/* Base Plan Row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingBottom: '0.5rem', borderBottom: '1px solid #e2e8f0' }}>
            <div>
              <div style={{ fontWeight: 400, color: '#0f172a' }}>
                Base Plan ({seats} {seats === 1 ? 'user seat' : 'user seats'})
              </div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '1px', fontWeight: 400 }}>
                ₹{perUserRate}/user per month {billingCycle === 'annual' ? '× 12 months' : ''}
              </div>
            </div>
            <div style={{ fontWeight: 400, color: '#0f172a' }}>
              ₹{baseRecurring.toLocaleString('en-IN')}
            </div>
          </div>

          {/* Selected Add-ons */}
          {selectedAddonsList.map((addon) => (
            <div key={addon.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingBottom: '0.5rem', borderBottom: '1px solid #e2e8f0' }}>
              <div>
                <div style={{ fontWeight: 400, color: '#0f172a' }}>
                  {addon.name}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '1px', fontWeight: 400 }}>
                  {addon.scope === 'one-time'
                    ? 'One-time setup / asset grant'
                    : addon.key === 'liveTracking'
                    ? `₹${addon.price}/user per month × ${seats} users ${billingCycle === 'annual' ? '× 12 months' : ''}`
                    : `₹${addon.price.toLocaleString('en-IN')}/month ${billingCycle === 'annual' ? '× 12 months' : ''}`}
                </div>
              </div>
              <div style={{ fontWeight: 400, color: '#0f172a' }}>
                ₹{addon.calculatedAmount.toLocaleString('en-IN')}
              </div>
            </div>
          ))}

          {/* Annual Discount Row */}
          {billingCycle === 'annual' && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingBottom: '0.5rem', borderBottom: '1px solid #e2e8f0', color: '#15803d' }}>
              <div>
                <div style={{ fontWeight: 400 }}>
                  Annual Commitment Discount (23% Off)
                </div>
                <div style={{ fontSize: '0.75rem', color: '#16a34a', marginTop: '1px', fontWeight: 400 }}>
                  Saving ₹1,800 per user every year by paying annually
                </div>
              </div>
              <div style={{ fontWeight: 400 }}>
                - ₹{annualSavingsTotal.toLocaleString('en-IN')}
              </div>
            </div>
          )}
        </div>

        {/* Perforated Tear Line with Circular Notches */}
        <div className="receipt-tear-line">
          <div className="receipt-cutout-left" />
          <div className="receipt-cutout-right" />
        </div>

        {/* Grand Total Row (Unbolded) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', paddingTop: '0.25rem' }}>
          <div>
            <div style={{ fontSize: '0.8125rem', fontWeight: 500, color: '#64748b', textTransform: 'none' }}>
              Total calculated cost
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginTop: '2px' }}>
              <span style={{ fontSize: '2.5rem', fontWeight: 400, color: '#0f172a', letterSpacing: '-0.02em', lineHeight: 1 }}>
                ₹{total.toLocaleString('en-IN')}
              </span>
              <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 400 }}>
                {billingCycle === 'annual' ? '(12 months upfront)' : '/ month (no lock-in)'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '6px' }}>
              <span style={{ fontSize: '11px', fontWeight: 400, background: '#ffffff', border: '1px solid #cbd5e1', padding: '2px 8px', borderRadius: '4px', color: '#334155' }}>
                ≈ ₹{monthlyEquivalent.toLocaleString('en-IN')}/mo equivalent
              </span>
              {billingCycle === 'annual' && (
                <span style={{ fontSize: '11px', fontWeight: 400, background: '#dcfce7', border: '1px solid #bbf7d0', padding: '2px 8px', borderRadius: '4px', color: '#15803d' }}>
                  23% savings applied
                </span>
              )}
              <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 400 }}>· Standard GST invoice applicable</span>
            </div>
          </div>

          {/* Action Tools */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleCopySalesPitch}
              className="btn-secondary-3d"
              style={{ fontWeight: 400 }}
            >
              <span>Copy Sales Pitch</span>
            </button>

            <button
              type="button"
              onClick={handleCopyQuotation}
              className="btn-primary-3d"
              style={{ fontWeight: 400 }}
            >
              <span>Copy Client Proposal</span>
            </button>
          </div>
        </div>
      </div>

      {/* ================================================================= */}
      {/* 6. COMPLETE 11 MODULES & 55 FEATURES FULL DIRECTORY               */}
      {/* ================================================================= */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.75rem', marginBottom: '1rem' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 400, color: '#0f172a' }}>
              Core Platform Features Directory ({totalFeatures} Features Across 11 Modules{apiDefinitionsCount > 0 ? ` · ${apiDefinitionsCount} Backend Definitions` : ''})
            </h2>
            <p style={{ margin: '0.2rem 0 0', fontSize: '0.82rem', color: '#64748b', fontWeight: 400 }}>
              All 11 modules are bundled into the base platform license with full capabilities:
            </p>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={allAreExpanded ? collapseAll : expandAll}
              className="btn-secondary-3d"
              style={{ padding: '6px 14px', fontSize: '12px', fontWeight: 400 }}
            >
              <span>{allAreExpanded ? 'Collapse All' : 'Expand All'}</span>
            </button>
          </div>
        </div>

        {/* Live Search Input */}
        <div style={{ position: 'relative', marginBottom: '1rem' }}>
          <input
            type="text"
            placeholder="Search platform capabilities (e.g. Invoicing, Meta Ads, Follow-ups, SOPs, GPS, Roles)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              height: '42px',
              padding: '0 80px 0 40px',
              fontSize: '0.86rem',
              fontWeight: 400,
              border: '1px solid #cbd5e1',
              borderRadius: '9999px',
              background: '#ffffff',
              outline: 'none',
              color: '#0f172a'
            }}
          />
          <Search size={16} color="#94a3b8" style={{ position: 'absolute', left: '14px', top: '13px' }} />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute',
                right: '8px',
                top: '7px',
                padding: '4px 10px',
                fontSize: '11px',
                fontWeight: 400,
                border: '1px solid #cbd5e1',
                borderRadius: '9999px',
                background: '#f8fafc',
                cursor: 'pointer'
              }}
            >
              Clear
            </button>
          )}
        </div>

        {/* Accordion Directory */}
        <div className="modern-plans-card" style={{ display: 'flex', flexDirection: 'column', borderRadius: '4px' }}>
          {filteredCategories.map((c, idx) => {
            const isOpen = !!expandedCategories[c.title] || searchQuery.length > 0;
            return (
              <div key={c.title} style={{ borderBottom: idx < filteredCategories.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                <button
                  type="button"
                  onClick={() => toggleCategory(c.title)}
                  style={{
                    width: '100%',
                    padding: '1rem 1.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease'
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8fafc')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontWeight: 400, fontSize: '0.95rem', color: '#0f172a' }}>
                      {c.title}
                    </span>
                    <span style={{ fontSize: '11px', color: '#64748b', background: '#f1f5f9', padding: '2px 8px', borderRadius: '9999px', fontWeight: 400 }}>
                      {c.items.length} {c.items.length === 1 ? 'feature' : 'features'}
                    </span>
                  </div>
                  {isOpen ? <ChevronUp size={16} color="#64748b" /> : <ChevronDown size={16} color="#64748b" />}
                </button>

                {isOpen && (
                  <div style={{ padding: '0.75rem 1.25rem 1.25rem', background: '#f8fafc', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.65rem' }}>
                    {c.items.map((item) => (
                      <div key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                        <Check size={14} color="#16a34a" strokeWidth={2.2} style={{ marginTop: '3px', flexShrink: 0 }} />
                        <span style={{ fontSize: '0.8rem', color: '#334155', lineHeight: 1.4, fontWeight: 400 }}>
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
};
export default PlansManagementView;
